import type { Maybe, Response, Setting, StoreThis } from '@/types'

import { ref } from 'vue'

import { defineStore } from 'pinia'

import { findByPropValue, handleErrors, findIndexByPropValue } from '@/helpers'

import SettingModel from '@/models/SettingModel'

type Type = Setting

const resource = 'settings'
const storeId = `customer-${resource}`

const Model = SettingModel

export const useCustomerSettingsStore = defineStore(storeId, () => {
  const items = ref<Type[]>([])
  const isLoading = ref(false)

  async function fetchItems(this: StoreThis) {
    isLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<Type[]> = await this.rest.get(`/c/${resource}`)

      if (!success && errors) throw Error(handleErrors(errors))

      items.value = data ?? []
    } catch (e) {
      console.error(`[${storeId}/fetchItems]`, e)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchItemByName(this: StoreThis, name: Type['name']) {
    let error = false

    isLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<Type> = await this.rest.get(`/c/${resource}/${name}`)

      if (!success && errors) throw Error(handleErrors(errors))

      const index = findIndexByPropValue(items.value, name, 'name')

      if (index !== -1) {
        Model.reset(items.value[index], data)
      } else items.value.push(Model.mergeTogether(data))
    } catch (e) {
      error = true

      console.error(`[${storeId}/fetchItemByName]`, e)
    } finally {
      isLoading.value = false
    }

    return { error }
  }

  function getItemByName(name?: Maybe<Type['name']>): Maybe<Type> {
    return name ? findByPropValue(items.value, name, 'name') : null
  }

  return {
    items,

    isLoading,

    fetchItems,
    fetchItemByName,

    getItemByName,
  }
})
